// frontend/src/games/hooks/useZodiacCrushMapProgress.js
//
// Purpose:
// - Fetch ZodiacCrush map progress from backend (GET /api/games/zodiac-crush/map/progress)
// - Provide a small, resilient cache so the map UI can render instantly.
//
// Progress shape used across the app:
// {
//   currentLevel: number,           // "next playable" pointer (server may return it)
//   maxUnlockedLevel: number,       // highest unlocked level
//   starsByLevel: { [level: string]: number }
// }

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authenticatedRequest } from '../../services/authApi';
import { localizedServerContent, localizedValue } from '../../localization/presentationLanguage';
import { getAuthUserSnapshot } from '../../utils/auth';
import { REST_API_ORIGIN } from '../../platform/endpoints';
const zcText = (es, en) => localizedValue({ es, en });
const localizedProgressError = (value) => {
  const message = String(value || '').trim();
  if (!message) return zcText('No se pudo cargar el progreso', 'Progress could not be loaded');
  return /[áéíóúñ¿¡]|\b(?:progreso|cargar|sesión|conexión)\b/i.test(message)
    ? localizedServerContent(message, 'Progress could not be loaded')
    : message;
};
// Local token helper (avoids hard dependency on a shared auth module)
// Picks the best non-expired JWT among a few common localStorage keys.
const getStoredToken = () => {
  const KEYS = ['token', 'accessToken', 'authToken', 'zodiacus_token', 'zodiacusToken'];

  const safeGet = (k) => {
    try { return String(localStorage.getItem(k) || ''); } catch { return ''; }
  };

  const decodeJwtPayload = (jwt) => {
    try {
      const parts = String(jwt || '').split('.');
      if (parts.length < 2) return null;
      // base64url -> base64
      let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      const json = atob(b64);
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  const nowSec = Math.floor(Date.now() / 1000);

  // Gather candidates, prefer longer strings (real JWTs) and non-expired.
  const candidates = KEYS
    .map((k) => safeGet(k).trim())
    .filter(Boolean)
    .sort((a, b) => (b.length || 0) - (a.length || 0));

  // Track whether we saw at least one token with an `exp` claim.
  let sawExp = false;

  for (const t of candidates) {
    const payload = decodeJwtPayload(t);
    const exp = Number(payload?.exp || 0) || 0;

    if (exp) sawExp = true;

    // If token has exp, require it to be valid for at least 30s.
    if (exp && exp > nowSec + 30) return t;
  }

  // If we saw tokens with exp but none are valid, return empty (prevents pointless 401 retries).
  if (sawExp) return '';

  // If none had exp (opaque tokens), fall back to first non-empty.
  return candidates[0] || '';
};

const getApiBaseCandidates = () => {
  return [REST_API_ORIGIN];
};

const stripBearer = (t) => String(t || '').replace(/^Bearer\s+/i, '').trim();

const looksLikeHtml = (txt) => {
  const s = String(txt || '').trim().slice(0, 200).toLowerCase();
  return s.startsWith('<!doctype') || s.startsWith('<html') || s.includes('<div id="root"');
};

const LEGACY_CACHE_KEY = 'zc_map_progress_cache_v1';
const CACHE_KEY_PREFIX = 'zc_map_progress_cache_v2';
export const ZC_MAP_PROGRESS_EVENT = 'zc_map_progress_updated';

// Shared by every mounted map/progress consumer. The entry is account-scoped,
// so StrictMode and the map/constellation overlap share one network operation
// without allowing one account's response to satisfy another account.
const progressRequestsByAccount = new Map();
let progressRequestId = 0;

const safeJsonParse = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const clampInt = (n, min, max) => {
  const v = Math.floor(Number(n || 0));
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
};

const normalizeProgress = (snap) => {
  // Accept a few possible server shapes
  const p =
    snap?.progress ||
    snap?.mapProgress ||
    snap?.profile?.zodiacCrushMapProgress ||
    snap?.profile?.mapProgress ||
    snap;

  const currentLevel = clampInt(p?.currentLevel ?? 1, 1, 240);

  // Server may sometimes return a bogus `maxUnlockedLevel` (we've observed 240 while the user is around level ~40).
  // If we blindly trust it, the UI can jump the player to /play?level=240.
  const rawMaxUnlocked = clampInt(p?.maxUnlockedLevel ?? currentLevel ?? 1, 1, 240);

  const rawStars = (p?.starsByLevel && typeof p.starsByLevel === 'object') ? p.starsByLevel : {};
  let maxStarLevel = 0;
  try {
    for (const k of Object.keys(rawStars || {})) {
      const lv = clampInt(k, 1, 240);
      if (lv > maxStarLevel) maxStarLevel = lv;
    }
  } catch {
    maxStarLevel = 0;
  }

  // Suspicious if server claims 240 but we are far below and our recorded stars don't go anywhere near 240.
  // Keep it conservative so legit late-game users are unaffected.
  const isSuspiciousMax =
    rawMaxUnlocked >= 240 &&
    currentLevel <= 200 &&
    maxStarLevel <= 200 &&
    (rawMaxUnlocked - currentLevel) >= 40;

  const maxUnlockedFixed = clampInt(
    Math.max(currentLevel, isSuspiciousMax ? Math.max(currentLevel, maxStarLevel) : rawMaxUnlocked),
    1,
    240
  );

  const starsByLevel = {};

  for (const [k, v] of Object.entries(rawStars || {})) {
    // levels are stored as string keys
    const lv = clampInt(k, 1, 240);
    const st = clampInt(v, 0, 3);
    starsByLevel[String(lv)] = st;
  }

  return { currentLevel, maxUnlockedLevel: maxUnlockedFixed, starsByLevel };
};

// Merge progress defensively: never allow maxUnlockedLevel or currentLevel to go DOWN due to stale cache / out-of-order.
const mergeProgress = (prevSnap, nextSnap) => {
  const prevN = normalizeProgress(prevSnap || { currentLevel: 1, maxUnlockedLevel: 1, starsByLevel: {} });
  const nextN = normalizeProgress(nextSnap || { currentLevel: 1, maxUnlockedLevel: 1, starsByLevel: {} });

  const merged = {
    // Never downgrade currentLevel / maxUnlockedLevel
    currentLevel: Math.max(
      Number(prevN.currentLevel || 1) || 1,
      Number(nextN.currentLevel || 1) || 1
    ),
    maxUnlockedLevel: Math.max(
      Number(prevN.maxUnlockedLevel || 1) || 1,
      Number(nextN.maxUnlockedLevel || 1) || 1
    ),
    starsByLevel: { ...(prevN.starsByLevel || {}) },
  };

  // Stars: keep the highest per level
  for (const [lv, st] of Object.entries(nextN.starsByLevel || {})) {
    const cur = Number(merged.starsByLevel[lv] || 0) || 0;
    const nxt = Number(st || 0) || 0;
    if (nxt > cur) merged.starsByLevel[lv] = clampInt(nxt, 0, 3);
  }

  merged.currentLevel = clampInt(merged.currentLevel, 1, 240);
  merged.maxUnlockedLevel = clampInt(Math.max(merged.maxUnlockedLevel, merged.currentLevel), 1, 240);
  return merged;
};

export const zcMapProgressCacheKeyForUser = (userId) => {
  const id = String(userId || '').trim();
  return id ? `${CACHE_KEY_PREFIX}:${encodeURIComponent(id)}` : null;
};

const emptyProgress = () => ({
  currentLevel: 1,
  maxUnlockedLevel: 1,
  starsByLevel: {},
});

const currentAuthenticatedUserId = () => {
  const snapshot = getAuthUserSnapshot();
  return String(snapshot?.id || '').trim();
};

const readCache = (userId) => {
  const key = zcMapProgressCacheKeyForUser(userId);
  if (!key) return emptyProgress();
  try {
    const raw = localStorage.getItem(key) || '{}';
    const cur = safeJsonParse(raw, {});
    return normalizeProgress(cur);
  } catch {
    return emptyProgress();
  }
};

const writeCache = (userId, progress, { replace = false } = {}) => {
  const key = zcMapProgressCacheKeyForUser(userId);
  if (!key) return;
  try {
    const next = replace
      ? normalizeProgress(progress)
      : mergeProgress(safeJsonParse(localStorage.getItem(key) || '{}', {}), progress);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore
  }
};

const fetchProgressSnapshot = async (signal) => {
        const path = '/api/games/zodiac-crush/map/progress';

        // Prefer explicit backend bases first (avoids CRA/proxy returning index.html)
        // If you really want to try same-origin in dev, set: localStorage zc_try_same_origin_api=1
        const trySameOrigin = (() => {
          try { return localStorage.getItem('zc_try_same_origin_api') === '1'; } catch (_) { return false; }
        })();

        const bases = getApiBaseCandidates();
        const abs = bases.map((b) => `${b}${path}`);
        const candidates = trySameOrigin ? [...abs, path] : abs;

        const token = stripBearer(getStoredToken());

        let lastErr = null;
        let data = null;
        let resp = null;

        for (const url of candidates) {
          try {
            const isSameOriginPath = (url === path);
            const isAbsolute = /^https?:\/\//i.test(url);
            const isSameOriginAbs = (() => {
              if (!isAbsolute) return true;
              try { return new URL(url).origin === window.location.origin; } catch (_) { return false; }
            })();
            const sendCookies = isSameOriginPath || isSameOriginAbs;

            resp = await authenticatedRequest(url, {
              rawResponse: true,
              method: 'GET',
              cache: 'no-store',
              ...(signal ? { signal } : {}),
              headers: {
                Accept: 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              ...(sendCookies ? { credentials: 'include' } : { credentials: 'omit', mode: 'cors' }),
              diagSource: 'useZodiacCrushMapProgress',
            });

            const ct = String(resp.headers.get('content-type') || '').toLowerCase();
            let txt = '';
            try { txt = await resp.text(); } catch (_) { txt = ''; }

            // Non-API trap (CRA dev server, proxies, misroutes): can return HTML or non-JSON with 200.
            // Treat as candidate failure and try the next base.
            const isNonJson = !ct.includes('json');
            const isHtmlish = looksLikeHtml(txt);
            const hasBody = String(txt || '').trim().length > 0;

            if (hasBody && (isHtmlish || isNonJson)) {
              lastErr = new Error(`Non-API response (HTTP ${resp.status})`);
              continue;
            }

            // Parse JSON from text (safer than resp.json() when the body is empty)
            let parsed = null;
            try { parsed = txt ? JSON.parse(txt) : null; } catch (_) { parsed = null; }

            // Empty or unparsable JSON is not a valid success payload; try next candidate.
            if (!parsed || typeof parsed !== 'object') {
              lastErr = new Error(`Invalid JSON payload (HTTP ${resp.status})`);
              continue;
            }

            // Decide whether to retry another base
            if (!resp.ok || (parsed && parsed.ok === false)) {
  const msg = parsed?.message || parsed?.error || `HTTP ${resp.status}`;

  // ✅ ESTO ES CLAVE → 401 NO corta, prueba siguiente candidato
  if (resp.status === 401 || resp.status === 403) {
    lastErr = new Error(msg || 'No autenticado');
    continue;
  }

  throw new Error(msg);
}

            // Success
            data = parsed;
            break;
          } catch (e) {
            if (e?.name === 'AbortError') throw e;

            lastErr = e;
            const m = String(e?.message || e || '');
            const isFetchType = (e instanceof TypeError) || /failed to fetch|networkerror|load failed|fetch/i.test(m);
            if (!isFetchType) throw e;

            // continue
          }
        }

        if (!data) {
          const msg = localizedProgressError(lastErr?.message || lastErr);
          throw new Error(msg);
        }

        return normalizeProgress(data);
};

const acquireProgressRequest = (userId, { force = false } = {}) => {
  const existing = progressRequestsByAccount.get(userId);
  if (!force && existing) return existing.promise;

  if (force && existing?.controller) existing.controller.abort();
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const requestId = ++progressRequestId;
  const entry = {
    requestId,
    controller,
    promise: null,
  };
  entry.promise = fetchProgressSnapshot(controller?.signal).finally(() => {
    if (progressRequestsByAccount.get(userId)?.requestId === requestId) {
      progressRequestsByAccount.delete(userId);
    }
  });
  progressRequestsByAccount.set(userId, entry);
  return entry.promise;
};

/**
 * Account-owned Zodiac Crush progress. Cached optimistic values are allowed to
 * render immediately for the same account; every accepted server response
 * replaces that cache because the server is authoritative.
 */
export default function useZodiacCrushMapProgress(opts = {}) {
  const { auto = true } = (opts && typeof opts === 'object') ? opts : {};
  const userId = currentAuthenticatedUserId();
  const initial = useMemo(() => readCache(userId), [userId]);
  const [progress, setProgress] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const ownerRef = useRef(userId);
  const requestSequenceRef = useRef(0);
  const localRequestRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
  }, []);

  const setLocalProgress = useCallback((nextOrFn) => {
    const owner = ownerRef.current;
    if (!owner) return;
    setProgress((prev) => {
      const next = typeof nextOrFn === 'function' ? nextOrFn(prev) : nextOrFn;
      const merged = mergeProgress(prev, next);
      writeCache(owner, merged);
      return merged;
    });
  }, []);

  const refetch = useCallback((requestOptions = {}) => {
    const owner = userId;
    if (!owner) return Promise.resolve(null);
    const force = requestOptions?.force === true;
    if (
      !force &&
      localRequestRef.current?.owner === owner &&
      localRequestRef.current?.promise
    ) {
      return localRequestRef.current.promise;
    }

    const sequence = ++requestSequenceRef.current;
    setLoading(true);
    setError(null);

    const promise = acquireProgressRequest(owner, { force })
      .then((normalized) => {
        if (
          !mountedRef.current ||
          ownerRef.current !== owner ||
          requestSequenceRef.current !== sequence
        ) return null;
        writeCache(owner, normalized, { replace: true });
        setProgress(normalized);
        return normalized;
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return null;
        if (
          mountedRef.current &&
          ownerRef.current === owner &&
          requestSequenceRef.current === sequence
        ) {
          setError(localizedProgressError(e?.message));
        }
        return null;
      })
      .finally(() => {
        if (
          localRequestRef.current?.promise === promise
        ) {
          localRequestRef.current = null;
        }
        if (
          mountedRef.current &&
          ownerRef.current === owner &&
          requestSequenceRef.current === sequence
        ) {
          setLoading(false);
        }
      });

    localRequestRef.current = { owner, promise };
    return promise;
  }, [userId]);

  useEffect(() => {
    ownerRef.current = userId;
    requestSequenceRef.current += 1;
    localRequestRef.current = null;
    setProgress(readCache(userId));
    setError(null);
    setLoading(false);
    if (auto && userId) void refetch();
  }, [auto, refetch, userId]);

  useEffect(() => {
    const onProgressUpdated = (event) => {
      const detail = event?.detail;
      if (detail && ownerRef.current) setLocalProgress(detail);
      if (detail?.authoritative === false) return;
      void refetch();
    };
    const onFocus = () => {
      void refetch();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    const onPageShow = () => {
      void refetch();
    };

    window.addEventListener(ZC_MAP_PROGRESS_EVENT, onProgressUpdated);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener(ZC_MAP_PROGRESS_EVENT, onProgressUpdated);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [refetch, setLocalProgress]);

  // Account changes can render before the reset effect commits. Never expose
  // the previous owner's state during that intervening render.
  const visibleProgress = ownerRef.current === userId ? progress : initial;

  return {
    progress: visibleProgress,
    loading,
    error,
    refetch,
    setLocalProgress,
  };
}

// Also export the cache helpers for rare advanced usage (optional)
export const zcMapProgressCache = {
  read: (userId = currentAuthenticatedUserId()) => readCache(userId),
  write: (progress, userId = currentAuthenticatedUserId()) => writeCache(userId, progress),
  normalize: normalizeProgress,
};

export const zcMapProgressLegacyCacheKey = LEGACY_CACHE_KEY;
export const zcMapProgressCacheKey = CACHE_KEY_PREFIX;
export const zcMapProgressSafeJsonParse = safeJsonParse;
